/**
 * Extension -> MIME mapping.
 *
 * Deliberately hand-rolled rather than pulled from `mime-types`: this archive
 * holds a narrow, known set of formats, and the camera-specific ones that
 * actually matter here (MTS from Sony camcorders, INSV from Insta360, DNG and
 * the raw formats from the photo team) are exactly the ones generic databases
 * get wrong or omit.
 */
const MIME_BY_EXT: Record<string, string> = {
  // --- video ---
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  webm: 'video/webm',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  m2ts: 'video/mp2t',
  mts: 'video/mp2t',
  ts: 'video/mp2t',
  '3gp': 'video/3gpp',
  insv: 'video/mp4', // Insta360 360-degree video, MP4 container
  // Dahua CCTV recorders. A proprietary wrapper around what is usually an
  // ordinary H.264 or H.265 elementary stream, which is why ffmpeg can often
  // read it once told what to expect. Given a type at all so it is classified
  // as video rather than falling through to octet-stream, where nothing would
  // thumbnail it, transcode it or index it.
  dav: 'video/x-dahua-dav',

  // --- image ---
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  insp: 'image/jpeg', // Insta360 still

  // --- raw stills ---
  dng: 'image/x-adobe-dng',
  cr2: 'image/x-canon-cr2',
  cr3: 'image/x-canon-cr3',
  nef: 'image/x-nikon-nef',
  arw: 'image/x-sony-arw',
  orf: 'image/x-olympus-orf',
  raf: 'image/x-fuji-raf',
  rw2: 'image/x-panasonic-rw2',

  // --- audio ---
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  flac: 'audio/flac',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  opus: 'audio/opus',

  // --- documents that ride along with a launch ---
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
  json: 'application/json',
  zip: 'application/zip',
};

export function mimeForExtension(ext: string | null): string {
  if (!ext) return 'application/octet-stream';
  return MIME_BY_EXT[ext.replace('.', '').toLowerCase()] ?? 'application/octet-stream';
}

export type AssetKind = 'image' | 'video' | 'audio' | 'other';

export function detectKind(mime: string): AssetKind {
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  return 'other';
}

/**
 * Formats browsers can decode natively. Anything false here needs a transcoded
 * proxy before it can be previewed in the web UI — which is most of what comes
 * off a real camera.
 */
export function isWebPlayable(mime: string): boolean {
  return [
    'video/mp4',
    'video/webm',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/avif',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
  ].includes(mime);
}

/** RAW stills need a demosaic pass before a thumbnail can be produced. */
export function isRawStill(mime: string): boolean {
  return mime.startsWith('image/x-') || mime === 'image/x-adobe-dng';
}

/**
 * Whether re-deriving an asset's type from its filename is an improvement.
 *
 * Reclassification runs on every reindex, and the filename is not a reliable
 * source: renaming is a permitted action, and a file renamed without its
 * extension re-derives as octet-stream. Applying that would take a working
 * video and classify it as 'other', which silently removes it from
 * thumbnailing, transcoding and search -- turning a cosmetic rename into a
 * disappearance.
 *
 * So a classification is only replaced when it says more than the one already
 * stored, never less.
 */
export function isBetterClassification(
  current: { mime: string; kind: AssetKind },
  next: { mime: string; kind: AssetKind },
): boolean {
  if (current.mime === next.mime && current.kind === next.kind) return false;
  // Never trade a known kind for an unknown one.
  if (next.kind === 'other' && current.kind !== 'other') return false;
  return true;
}

/**
 * Containers ffmpeg cannot identify on its own.
 *
 * These hold an ordinary elementary stream behind a proprietary header, so
 * ffmpeg reads them once told what to expect but fails outright otherwise.
 * Checked before spending a probe on the fallback chain, because that probe
 * would otherwise run against every video in the archive to help the handful
 * that need it.
 *
 * Add a mime here when a recorder's output ingests with no thumbnail and no
 * duration while the file plays fine elsewhere.
 */
export function mayNeedFormatHint(mime: string): boolean {
  return mime === 'video/x-dahua-dav';
}

/**
 * Stills ffmpeg cannot be relied on to decode.
 *
 * Camera raw is sensor data and needs demosaicing, which ffmpeg does not do at
 * all. HEIC -- what every iPhone writes by default -- it decodes only when
 * built against libheif, which most distribution builds are not, and the
 * failure is a silent empty output rather than an error. Both are handed to
 * the Python worker instead, which needs LibRaw and libheif anyway in order to
 * run detection on these files.
 */
export function needsWorkerDecode(mime: string): boolean {
  return isRawStill(mime) || mime === 'image/heic' || mime === 'image/heif';
}
